// Operación del día. Lo primero que un jefe de operación pide: cuánto entró,
// cuánto se retiró, cuánto se estanteó, cuánto salió, cuánto se entregó; qué
// avisos hay abiertos; cómo van los recorridos y los choferes; qué hay en cada
// estantería. Todo sale de lo que el sistema ya registra: esta pantalla no
// inventa datos, los junta.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type Paso = { paso: string; n: number };
// Del Turbo automático: solo lo que hace falta para el bloque del día.
type Turbo = { route_id: number; status: "OFFERING" | "ASSIGNED" | "ESCALATED" | "CANCELLED"; route_status: string; waiting_seconds: number | null };
type Resumen = {
  fecha: string;
  embudo: Paso[];
  otros: { NOT_DELIVERED: number; CANCELLED: number };
  recorridos: { type: string; status: string; n: number }[];
  avisos: { flex_sin_en_camino: number; geo_pendientes: number; cp_no_catalogado: number; fuera_de_lugar: number };
  choferes: { activos: number; reportando: number; en_recorrido: number };
  por_hora: { hora: number; COLLECTED: number; SHELVED: number; OUT_FOR_DELIVERY: number; DELIVERED: number }[];
  stock: { code: string; unclassified: boolean; n: number }[];
};

const PASO_LABEL: Record<string, string> = {
  ENTERED: "Dados de alta", COLLECTED: "Retirados", ARRIVED_WAREHOUSE: "En planta", SHELVED: "Estanteados",
  ASSIGNED_TO_ROUTE: "En recorrido", OUT_FOR_DELIVERY: "En la calle", DELIVERED: "Entregados",
};
const ROUTE_LABEL: Record<string, string> = { PICKUP: "Colectas", DELIVERY: "Repartos", TURBO: "Turbo" };
const STATUS_LABEL: Record<string, string> = { PLANNED: "planificados", ASSIGNED: "asignados", LOADED: "cargados", IN_PROGRESS: "en curso", COMPLETED: "terminados", CANCELLED: "cancelados" };
const SERIE = [
  { k: "COLLECTED", label: "retirados", color: "#4f8cff" }, { k: "SHELVED", label: "estanteados", color: "#d29922" },
  { k: "OUT_FOR_DELIVERY", label: "en la calle", color: "#a371f7" }, { k: "DELIVERED", label: "entregados", color: "#3fb950" },
] as const;

// «Hoy» es el día del DEPÓSITO, y lo sabe el servidor: pedir el resumen sin
// fecha devuelve el de hoy con su `fecha`. Calcularlo en el navegador estaba
// mal: un navegador en otra zona horaria pedía el día siguiente y veía ceros.

/* ===== Estilos (mismo lenguaje que PlantaPage.tsx) ===== */
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "24px", fontWeight: 700, color: "var(--text-primary)" };
const subtitleStyle: React.CSSProperties = { margin: "8px 0 0 0", color: "var(--text-secondary)", fontSize: "15px", lineHeight: 1.6 };
const smallMutedStyle: React.CSSProperties = { fontSize: "12px", color: "var(--text-secondary)" };
const sectionStyle: React.CSSProperties = { ...smallMutedStyle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "10px" };
const kpiStyle: React.CSSProperties = { fontSize: "30px", fontWeight: 800, lineHeight: 1.1, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" };
const chipStyle = (color: string): React.CSSProperties => ({ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 12px", borderRadius: "12px", border: `1px solid ${color}`, color: "var(--text-primary)", cursor: "pointer", background: "transparent" });
const WARN = "var(--warning, #d29922)";

export default function OperacionPage() {
  const { token, capabilities } = useAuth();
  const navigate = useNavigate();
  // Los turbos piden db_access en el servidor: sin ella el bloque no se pide
  // ni se muestra (antes quedaba en «Cargando…» por un 403 silencioso).
  const verTurbo = capabilities?.db_access === true;
  const [fecha, setFecha] = useState<string | null>(null);   // null = hoy, según el servidor
  const [hoyServidor, setHoyServidor] = useState<string | null>(null);
  const [r, setR] = useState<Resumen | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actualizado, setActualizado] = useState<Date | null>(null);
  const [turbos, setTurbos] = useState<Turbo[] | null>(null);
  const esHoy = fecha === null || fecha === hoyServidor;

  const cargar = useCallback(async () => {
    try {
      const res = await authFetch(`/v2/ops/day${fecha ? `?date=${fecha}` : ""}`, { token });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.ok === false) throw new Error(json?.error || `Error ${res.status}`);
      setR(json); setError(null); setActualizado(new Date());
      if (!fecha) setHoyServidor(json.fecha);
    } catch (e: any) { setError(e.message); }
  }, [token, fecha]);

  // Los turbos del día salen de su propio módulo; se piden con la misma fecha
  // que el resumen y al mismo ritmo.
  useEffect(() => {
    const f = r?.fecha;
    if (!f || !verTurbo) return;
    authFetch(`/v2/turbo/dispatches?date=${f}`, { token })
      .then(async (res) => { const json = await res.json().catch(() => ({})); if (res.ok && json?.ok !== false) setTurbos(json.dispatches ?? []); })
      .catch(() => {});
  }, [token, r?.fecha, actualizado, verTurbo]);

  // Hoy se refresca solo; un día pasado no cambia.
  useEffect(() => { cargar(); if (!esHoy) return; const t = window.setInterval(cargar, 30_000); return () => window.clearInterval(t); }, [cargar, esHoy]);

  const maxEmbudo = useMemo(() => Math.max(1, ...(r?.embudo.map((p) => p.n) ?? [1])), [r]);
  const maxHora = useMemo(() => Math.max(1, ...(r?.por_hora.flatMap((h) => SERIE.map((s) => h[s.k])) ?? [1])), [r]);
  const maxStock = useMemo(() => Math.max(1, ...(r?.stock.map((s) => s.n) ?? [1])), [r]);
  const recorridosPorTipo = useMemo(() => {
    const m: Record<string, Record<string, number>> = {};
    for (const x of r?.recorridos ?? []) { (m[x.type] ??= {})[x.status] = x.n; }
    return m;
  }, [r]);
  const avisosTotal = r ? Object.values(r.avisos).reduce((a, b) => a + b, 0) : 0;
  const turboResumen = useMemo(() => {
    const t = turbos ?? [];
    const sinChofer = t.filter((x) => x.status === "OFFERING" || x.status === "ESCALATED");
    return {
      total: t.length,
      sinChofer: sinChofer.length,
      escalados: t.filter((x) => x.status === "ESCALATED").length,
      enCurso: t.filter((x) => x.status === "ASSIGNED" && ["ASSIGNED", "LOADED", "IN_PROGRESS"].includes(x.route_status)).length,
      terminados: t.filter((x) => x.route_status === "COMPLETED").length,
      maxEsperaMin: Math.round(sinChofer.reduce((m, x) => Math.max(m, x.waiting_seconds ?? 0), 0) / 60),
    };
  }, [turbos]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "16px", flexWrap: "wrap", alignItems: "flex-start" }}>
          <div>
            <h2 style={titleStyle}>Operación del día</h2>
            <p style={subtitleStyle}>Cuánto entró, cuánto salió y qué quedó abierto. Sale de lo que el sistema registra; nada de acá se carga a mano.</p>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
            <input id="operacion-fecha" type="date" value={fecha ?? r?.fecha ?? ""} max={hoyServidor ?? undefined} onChange={(e) => setFecha(e.target.value && e.target.value !== hoyServidor ? e.target.value : null)}
              style={{ background: "transparent", color: "var(--text-primary)", border: "1px solid var(--border)", borderRadius: "10px", padding: "8px 10px", font: "inherit" }} />
            {!esHoy ? <Button variant="secondary" onClick={() => setFecha(null)}>Hoy</Button> : null}
            <Button variant="secondary" onClick={cargar}>Actualizar</Button>
          </div>
        </div>
        <div style={{ ...smallMutedStyle, marginTop: "10px" }}>
          {actualizado ? `Actualizado ${actualizado.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}${esHoy ? " · cada 30 s" : ""}` : "Cargando…"}
          {error ? <span style={{ color: "var(--danger)", marginLeft: "12px" }}>{error}</span> : null}
        </div>
      </Card>

      {/* El embudo: cada paso que el bulto cruza, con cuántos lo cruzaron hoy. */}
      <Card>
        <div style={sectionStyle}>El embudo de {esHoy ? "hoy" : r?.fecha}</div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${r?.embudo.length ?? 7}, minmax(0, 1fr))`, gap: "8px", alignItems: "end" }}>
          {(r?.embudo ?? []).map((p) => (
            <div key={p.paso} style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <div style={kpiStyle}>{p.n.toLocaleString("es-AR")}</div>
              <div style={{ height: "10px", borderRadius: "999px", background: "var(--border)", overflow: "hidden" }}>
                <div style={{ width: `${Math.round(100 * p.n / maxEmbudo)}%`, height: "100%", background: p.paso === "DELIVERED" ? "#3fb950" : "var(--primary, #4f8cff)" }} />
              </div>
              <div style={smallMutedStyle}>{PASO_LABEL[p.paso] ?? p.paso}</div>
            </div>
          ))}
        </div>
        {r && (r.otros.NOT_DELIVERED || r.otros.CANCELLED) ? (
          <div style={{ ...smallMutedStyle, marginTop: "12px" }}>
            Y además: <b style={{ color: "var(--text-primary)" }}>{r.otros.NOT_DELIVERED}</b> no entregados · <b style={{ color: "var(--text-primary)" }}>{r.otros.CANCELLED}</b> cancelados
          </div>
        ) : null}
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "20px" }}>
        {/* Avisos abiertos: estado actual, no solo de hoy. Cada uno lleva a su bandeja. */}
        <Card>
          <div style={sectionStyle}>Avisos abiertos · {avisosTotal}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {r ? [
              ["Flex sin «En camino»", r.avisos.flex_sin_en_camino, "/admin/deposito/planta"],
              ["Pendientes de geolocalización", r.avisos.geo_pendientes, "/admin/deposito/planta"],
              ["CP no catalogados", r.avisos.cp_no_catalogado, "/admin/deposito/planta"],
              ["Fuera de lugar", r.avisos.fuera_de_lugar, "/admin/deposito/planta"],
            ].map(([label, n, to]) => (
              <button key={String(label)} type="button" style={chipStyle(Number(n) ? WARN : "var(--border)")} onClick={() => navigate(String(to))}>
                <span style={{ fontSize: "20px", fontWeight: 800, fontVariantNumeric: "tabular-nums", color: Number(n) ? WARN : "var(--text-secondary)" }}>{Number(n).toLocaleString("es-AR")}</span>
                <span style={{ fontSize: "13px" }}>{label}</span>
              </button>
            )) : <span style={smallMutedStyle}>—</span>}
          </div>
          <div style={{ ...smallMutedStyle, marginTop: "10px" }}>Ninguno frena la operación; cada uno tiene su bandeja en la Planta.</div>
        </Card>

        {/* Recorridos y choferes del día. */}
        <Card>
          <div style={sectionStyle}>Recorridos de {esHoy ? "hoy" : r?.fecha}</div>
          {Object.keys(recorridosPorTipo).length === 0 ? <div style={smallMutedStyle}>Ningún recorrido con esta fecha.</div> :
            Object.entries(recorridosPorTipo).map(([type, estados]) => (
              <div key={type} style={{ display: "flex", gap: "10px", alignItems: "baseline", padding: "6px 0", borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
                <b style={{ color: "var(--text-primary)", minWidth: "80px" }}>{ROUTE_LABEL[type] ?? type}</b>
                <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                  {["PLANNED", "ASSIGNED", "LOADED", "IN_PROGRESS", "COMPLETED", "CANCELLED"].filter((s) => estados[s]).map((s) => `${estados[s]} ${STATUS_LABEL[s]}`).join(" · ")}
                </span>
              </div>
            ))}
          <div style={{ ...sectionStyle, marginTop: "16px" }}>Choferes</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px" }}>
            <div><div style={kpiStyle}>{r?.choferes.activos ?? "—"}</div><div style={smallMutedStyle}>activos {esHoy ? "hoy" : "ese día"}</div></div>
            <div><div style={{ ...kpiStyle, color: "var(--success)" }}>{r?.choferes.reportando ?? "—"}</div><div style={smallMutedStyle}>reportando ahora</div></div>
            <div><div style={kpiStyle}>{r?.choferes.en_recorrido ?? "—"}</div><div style={smallMutedStyle}>en recorrido</div></div>
          </div>
          <div style={{ marginTop: "10px" }}><Button variant="secondary" onClick={() => navigate("/admin/choferes/mapa")}>Ver el mapa en vivo</Button></div>
        </Card>
      </div>

      {/* Turbos del día: lo que urge es el que está sin chofer. */}
      {verTurbo ? <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
          <div style={sectionStyle}>Turbos de {esHoy ? "hoy" : r?.fecha} · {turboResumen.total}</div>
          <Button variant="secondary" onClick={() => navigate("/admin/turbo/despachos")}>Ver despachos</Button>
        </div>
        {turbos === null ? <div style={smallMutedStyle}>Cargando…</div> : turboResumen.total === 0 ? <div style={smallMutedStyle}>Ningún turbo {esHoy ? "hoy" : "ese día"}.</div> : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "10px" }}>
            <div><div style={{ ...kpiStyle, color: turboResumen.sinChofer ? WARN : "var(--text-primary)" }}>{turboResumen.sinChofer}</div><div style={smallMutedStyle}>sin chofer{turboResumen.sinChofer ? ` · el que más, ${turboResumen.maxEsperaMin} min` : ""}</div></div>
            <div><div style={{ ...kpiStyle, color: turboResumen.escalados ? "var(--danger)" : "var(--text-primary)" }}>{turboResumen.escalados}</div><div style={smallMutedStyle}>escalados a coordinación</div></div>
            <div><div style={kpiStyle}>{turboResumen.enCurso}</div><div style={smallMutedStyle}>con chofer, en curso</div></div>
            <div><div style={{ ...kpiStyle, color: "var(--success)" }}>{turboResumen.terminados}</div><div style={smallMutedStyle}>terminados</div></div>
          </div>
        )}
      </Card> : null}

      {/* Por hora: la forma del día. Barras SVG, sin librería. */}
      <Card>
        <div style={sectionStyle}>Por hora</div>
        <div style={{ overflowX: "auto" }}>
          <svg viewBox="0 0 960 220" width="100%" style={{ minWidth: "640px", display: "block" }} role="img" aria-label="Movimientos por hora">
            {(r?.por_hora ?? []).map((h, i) => {
              const x = 30 + i * 38;
              return (
                <g key={h.hora}>
                  {SERIE.map((s, j) => {
                    const alto = Math.round(160 * h[s.k] / maxHora);
                    return <rect key={s.k} x={x + j * 8} y={180 - alto} width={7} height={alto} fill={s.color} opacity={0.9}><title>{`${String(h.hora).padStart(2, "0")}:00 · ${s.label}: ${h[s.k]}`}</title></rect>;
                  })}
                  <text x={x + 16} y={200} fontSize="10" textAnchor="middle" fill="var(--text-secondary)">{String(h.hora).padStart(2, "0")}</text>
                </g>
              );
            })}
            <line x1="30" y1="180" x2="950" y2="180" stroke="var(--border)" />
            <text x="10" y="24" fontSize="10" fill="var(--text-secondary)">{maxHora}</text>
          </svg>
        </div>
        <div style={{ ...smallMutedStyle, display: "flex", gap: "16px", flexWrap: "wrap", marginTop: "6px" }}>
          {SERIE.map((s) => <span key={s.k}><i style={{ display: "inline-block", width: "10px", height: "10px", borderRadius: "2px", background: s.color, marginRight: "6px", verticalAlign: "middle" }} />{s.label}</span>)}
        </div>
      </Card>

      {/* Lo que hay en cada estantería ahora mismo (no solo lo de hoy). */}
      <Card>
        <div style={sectionStyle}>En las estanterías ahora</div>
        {!r || r.stock.length === 0 ? <div style={smallMutedStyle}>Nada estanteado.</div> : (
          <div style={{ display: "grid", gap: "6px" }}>
            {r.stock.map((s) => (
              <div key={s.code} style={{ display: "grid", gridTemplateColumns: "110px 1fr 60px", gap: "10px", alignItems: "center", fontSize: "13px" }}>
                <span style={{ fontFamily: "monospace", color: s.unclassified ? WARN : "var(--text-primary)" }}>{s.code}</span>
                <span style={{ height: "10px", borderRadius: "999px", background: "var(--border)", overflow: "hidden", display: "block" }}>
                  <span style={{ display: "block", height: "100%", width: `${Math.round(100 * s.n / maxStock)}%`, background: s.unclassified ? WARN : "var(--primary, #4f8cff)" }} />
                </span>
                <span style={{ textAlign: "right", fontVariantNumeric: "tabular-nums", color: "var(--text-primary)" }}>{s.n.toLocaleString("es-AR")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
